import { useState } from "react";
import { hotTrends } from "@/mocks/trending";

export default function TrendBoard() {
  const [liked, setLiked] = useState<Record<number, boolean>>({});

  const toggle = (id: number) => setLiked((p) => ({ ...p, [id]: !p[id] }));

  return (
    <section id="board" className="w-full bg-background-50 px-4 py-12 md:px-8 md:py-16 lg:px-14">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="mb-2 inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                <i className="ri-flashlight-fill" aria-hidden="true"></i> 每分钟都在更新
              </p>
              <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                今日刷屏 <span className="text-primary-500">热榜 TOP8</span>
              </h2>
            </div>
            <a href="#feed" className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm text-primary-700 hover:text-primary-800 sm:inline-flex">
              查看全部栏目 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </a>
          </div>

          <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
            {hotTrends.map((item, i) => (
              <article
                key={item.id}
                className="group flex items-center gap-3 border-b border-background-100 px-4 py-3.5 transition-colors last:border-0 hover:bg-background-100/70 md:gap-4 md:px-5"
              >
                <span
                  className={`w-8 shrink-0 text-center font-heading text-xl font-black ${
                    i < 3 ? "text-primary-500" : "text-secondary-400"
                  }`}
                >
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-700 md:text-base">{item.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                    <span className="whitespace-nowrap">{item.category}</span>
                    <span className="hidden sm:inline">·</span>
                    <span className="flex items-center gap-1 whitespace-nowrap text-primary-600">
                      <i className="ri-arrow-up-circle-line" aria-hidden="true"></i> {item.shares} 转发
                    </span>
                  </div>
                </div>
                <span className="hidden shrink-0 rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 md:inline">{item.tag}</span>
                <div className="hidden shrink-0 flex-col items-end lg:flex">
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {item.time}
                  </span>
                  <span className="mt-1 text-xs text-foreground-400">热度 {item.heat}</span>
                </div>
                <button
                  onClick={() => toggle(item.id)}
                  aria-label="点赞"
                  aria-pressed={!!liked[item.id]}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                    liked[item.id] ? "bg-primary-100 text-primary-600" : "text-foreground-400 hover:bg-background-100 hover:text-primary-600"
                  }`}
                >
                  <i className={`${liked[item.id] ? "ri-heart-fill" : "ri-heart-line"} text-lg`} aria-hidden="true"></i>
                </button>
              </article>
            ))}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 flex flex-col gap-6">
            <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-foreground-950 p-6 text-background-50">
              <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-accent-500/20"></div>
              <div className="absolute -bottom-8 -left-8 h-28 w-28 rounded-full bg-primary-500/25"></div>
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/20 px-3 py-1 text-xs font-medium text-accent-300">
                  <i className="ri-star-smile-fill" aria-hidden="true"></i> 编辑精选
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold leading-snug text-background-50">
                  为什么「一本正经地没用」反而最容易出圈？
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-background-100/85">
                  反差、仪式感、评论区共创——本期从三代爆款拆解出一套可复用的出圈公式。
                </p>
                <a href="#features" className="mt-5 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-accent-400 hover:text-accent-300">
                  去读复盘 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
                  <i className="ri-calendar-event-line" aria-hidden="true"></i>
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">追更时间表</h3>
              </div>
              <ul className="space-y-3 text-sm text-foreground-700">
                <li className="flex items-center justify-between gap-2">
                  <span className="whitespace-nowrap">热点速递 / 热榜</span>
                  <span className="whitespace-nowrap text-xs text-foreground-500">每日 09:00 起滚动更新</span>
                </li>
                <li className="flex items-center justify-between gap-2">
                  <span className="whitespace-nowrap">深度盘点</span>
                  <span className="whitespace-nowrap text-xs text-foreground-500">每周一 / 三 / 五</span>
                </li>
                <li className="flex items-center justify-between gap-2">
                  <span className="whitespace-nowrap">出圈梗合集</span>
                  <span className="whitespace-nowrap text-xs text-foreground-500">每周六晚</span>
                </li>
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}